import { Box, Button, Tooltip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../global/theme/tokens';
import CheckIcon from '@mui/icons-material/Check';
import WarningIcon from '@mui/icons-material/Warning';

/**
 * Multi select over the connected mailboxes. Cards instead of a dropdown so the
 * user sees at a glance which accounts will answer automatically — including
 * the ones that currently have a broken connection.
 */
export const OutOfOfficeMailboxPicker = ({
  mailboxes = [],
  selectedIds = [],
  onChange,
  error,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const allSelected =
    mailboxes.length > 0 && selectedIds.length === mailboxes.length;

  function toggle(id) {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((selectedId) => selectedId !== id)
        : [...selectedIds, id],
    );
  }

  return (
    <Box className="flex flex-col gap-1.5">
      <Box className="flex items-center justify-between gap-2">
        <Typography fontSize={12} fontWeight={600} className="opacity-70">
          {t('Reply from these mailboxes')}
          {selectedIds.length > 0 && (
            <Box
              component="span"
              sx={{ color: colors.info }}
              className="ml-2"
            >{`${selectedIds.length}/${mailboxes.length}`}</Box>
          )}
        </Typography>
        <Button
          size="small"
          onClick={() =>
            onChange(allSelected ? [] : mailboxes.map((mailbox) => mailbox.id))
          }
          sx={{ fontSize: '11px', minWidth: 0 }}
          disabled={!mailboxes.length}
        >
          {allSelected ? t('Clear all') : t('Select all')}
        </Button>
      </Box>

      {mailboxes.length === 0 ? (
        <Typography fontSize={12} className="opacity-60 py-2">
          {t('No mailboxes connected yet.')}
        </Typography>
      ) : (
        <Box className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {mailboxes.map((mailbox) => (
            <MailboxCard
              key={mailbox.id}
              mailbox={mailbox}
              isSelected={selectedIds.includes(mailbox.id)}
              onClick={() => toggle(mailbox.id)}
              colors={colors}
            />
          ))}
        </Box>
      )}

      {error && (
        <Typography fontSize={11} sx={{ color: colors.error }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

const MailboxCard = ({ mailbox, isSelected, onClick, colors }) => {
  const { t } = useTranslation();
  const { label, imapUsername, color, imapStatus } = mailbox;
  const accentColor = color || colors.info;

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        width: '100%',
        minWidth: 0,
        padding: '8px 10px',
        borderRadius: '8px',
        cursor: 'pointer',
        textAlign: 'left',
        transition: '0.2s ease',
        color: 'inherit',
        bgcolor: isSelected ? colors.info + '30' : colors.card,
        border: `1px solid ${isSelected ? colors.info + '80' : 'transparent'}`,
        ':hover': {
          bgcolor: isSelected ? colors.info + '40' : colors.info + '18',
          border: `1px solid ${colors.info}80`,
        },
      }}
    >
      <Box
        className="flex items-center justify-center shrink-0"
        sx={{
          width: '26px',
          height: '26px',
          borderRadius: '9999px',
          bgcolor: isSelected ? colors.info : accentColor + '40',
          border: `1px solid ${isSelected ? colors.info : accentColor}`,
          transition: '0.2s ease',
        }}
      >
        {isSelected ? (
          <CheckIcon sx={{ fontSize: '16px', color: '#fff' }} />
        ) : (
          <Typography fontSize={11} fontWeight={700}>
            {(label || imapUsername || '?').charAt(0).toUpperCase()}
          </Typography>
        )}
      </Box>

      <Box className="flex flex-col min-w-0 flex-1">
        <Box className="flex items-center gap-1 min-w-0">
          <Typography fontSize={13} fontWeight={600} noWrap>
            {label}
          </Typography>
          {imapStatus !== 1 && (
            <Tooltip title={t('Reauthentication required')}>
              <WarningIcon color="warning" sx={{ fontSize: '14px' }} />
            </Tooltip>
          )}
        </Box>
        <Typography fontSize={11} className="opacity-50" noWrap>
          {imapUsername}
        </Typography>
      </Box>
    </Box>
  );
};
